"use client";

import * as Sentry from "@sentry/nextjs";
import { useEffect } from "react";

/**
 * Dernier filet du rendu navigateur : Next n'appelle ce composant que lorsque
 * l'erreur survient dans le layout racine lui-même, là où aucun `error.tsx` de
 * segment ne peut l'attraper. Il REMPLACE le layout racine, d'où les balises
 * <html>/<body> et l'absence de tout composant de l'application : si le layout
 * vient de casser, en réimporter des morceaux casserait aussi la page d'erreur.
 *
 * Styles en ligne pour la même raison — `globals.css` peut faire partie de ce
 * qui a échoué. Le thème suit l'appareil : le script de thème du layout racine
 * n'a, par construction, pas tourné.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <html lang="fr">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1.5rem",
          fontFamily:
            "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
          background: "Canvas",
          color: "CanvasText",
        }}
      >
        <main style={{ maxWidth: "32rem", textAlign: "center" }}>
          <h1 style={{ fontSize: "1.5rem", marginBottom: "0.75rem" }}>
            Une erreur inattendue est survenue
          </h1>
          <p style={{ lineHeight: 1.6, opacity: 0.8 }}>
            L&apos;incident a été signalé à l&apos;équipe technique. Vos données
            n&apos;ont pas été perdues : réessayez, ou revenez à l&apos;accueil.
          </p>
          {/* `digest` est l'identifiant que Next donne à l'erreur côté serveur.
              C'est le seul lien qu'un utilisateur puisse citer au support pour
              qu'on retrouve l'issue correspondante. */}
          {error.digest ? (
            <p
              style={{
                marginTop: "1rem",
                fontSize: "0.8125rem",
                opacity: 0.6,
                fontFamily: "ui-monospace, monospace",
              }}
            >
              Référence : {error.digest}
            </p>
          ) : null}
          <div
            style={{
              marginTop: "1.5rem",
              display: "flex",
              gap: "0.75rem",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={reset}
              style={{
                padding: "0.625rem 1.25rem",
                borderRadius: "0.5rem",
                border: "1px solid currentColor",
                background: "transparent",
                color: "inherit",
                font: "inherit",
                cursor: "pointer",
              }}
            >
              Réessayer
            </button>
            {/* `<a>` et non `<Link>`, volontairement : `next/link` navigue
                côté client en réutilisant l'arbre React qui vient justement de
                casser — le clic ne mènerait nulle part. Un lien natif force un
                rechargement complet, seul moyen de repartir d'un état sain. */}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a
              href="/"
              style={{
                padding: "0.625rem 1.25rem",
                borderRadius: "0.5rem",
                border: "1px solid currentColor",
                color: "inherit",
                textDecoration: "none",
              }}
            >
              Retour à l&apos;accueil
            </a>
          </div>
        </main>
      </body>
    </html>
  );
}
